import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccount } from '@/api/useAccount';
import { formatDateOnly } from '@/lib/format';
import {
  InvoiceInvalidError,
  InvoiceRefusedError,
  useInvoiceReminders,
  type Invoice,
} from '@/routes/invoices/useInvoices';

/**
 * One invoice's payment reminders (M2-16, pass B): what will happen next, and
 * — for `customers.manage` — the per-invoice opt-out.
 *
 * The sentence is chosen from the server's facts only, in the order that
 * explains the most: a draft has not started; an account with no steps sends
 * none; an opted-out invoice gets none; a paid one needs none; no live link
 * (revoked, or never issued — sent before M2-16) pauses them (the composer
 * needs a live link); otherwise the API's
 * `nextReminderOn` — a `DateOnly` on the SHOP's calendar, so it is formatted
 * on UTC and never moves a day for a viewer west of the shop — or "no more".
 * The page computes no reminder date of its own.
 *
 * Not shown for a void invoice: the API refuses the switch there, and there
 * is nothing to remind anyone of.
 */
export function InvoiceReminders({
  invoice,
  canAct,
  onAnnounce,
}: {
  invoice: Invoice;
  canAct: boolean;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const save = useInvoiceReminders(invoice.id);
  const offsets = useAccount().data?.invoiceReminderOffsets;
  const [problem, setProblem] = useState<string | null>(null);
  const number = Number(invoice.number);

  if (invoice.status === 'Void') return null;

  const status =
    invoice.status === 'Draft'
      ? t('invoices.reminders.draft')
      : offsets !== undefined && offsets.length === 0
        ? t('invoices.reminders.accountOff')
        : !invoice.remindersEnabled
          ? t('invoices.reminders.optedOut')
          : invoice.status === 'Paid'
            ? t('invoices.reminders.paid')
            : !invoice.hasCustomerLink
              ? t('invoices.reminders.paused')
              : invoice.nextReminderOn
                ? t('invoices.reminders.next', { date: formatDateOnly(invoice.nextReminderOn) })
                : t('invoices.reminders.none');

  return (
    <section
      aria-labelledby={`${id}-heading`}
      className="space-y-2 rounded-lg border border-[var(--color-border)] p-4"
    >
      <h2 id={`${id}-heading`} className="text-base font-semibold">
        {t('invoices.reminders.heading')}
      </h2>
      {canAct ? (
        <label className="flex items-center gap-2 text-sm" htmlFor={`${id}-toggle`}>
          <input
            id={`${id}-toggle`}
            type="checkbox"
            checked={invoice.remindersEnabled}
            disabled={save.isPending}
            aria-describedby={`${id}-status`}
            onChange={(event) => {
              const enabled = event.target.checked;
              setProblem(null);
              save.mutate(enabled, {
                onSuccess: () =>
                  onAnnounce(
                    enabled
                      ? t('invoices.reminders.turnedOn', { number })
                      : t('invoices.reminders.turnedOff', { number }),
                  ),
                onError: (error) =>
                  setProblem(
                    error instanceof InvoiceRefusedError || error instanceof InvoiceInvalidError
                      ? t('invoices.acts.refused', { reason: error.message })
                      : t('invoices.acts.failed'),
                  ),
              });
            }}
          />
          <span>{t('invoices.reminders.toggle')}</span>
        </label>
      ) : null}
      <p
        id={`${id}-status`}
        className="text-sm text-[var(--color-muted-foreground)]"
        data-testid="reminder-status"
      >
        {status}
      </p>
      {problem ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {problem}
        </p>
      ) : null}
    </section>
  );
}
